import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Clock, CheckCircle, ExternalLink, Maximize2, Minimize2, DollarSign } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GoogleCalendarAuth from "@/components/GoogleCalendarAuth";
import { useAsanaIntegration, type AsanaTask } from "@/hooks/useAsanaIntegration";
import { useAuth } from "@/hooks/useAuth";
import { useMarketData, type MarketRow } from "@/hooks/useMarketData";
import { MarketStrip } from "@/components/market/MarketStrip";
import { useIsOnline } from "@/hooks/useIsOnline";
import { getCachedSync, setCached } from "@/lib/offline/persistentCache";
import { PostsScroller } from "@/components/PostsScroller";
import { MediaScroller } from "@/components/MediaScroller";
import HouseViewWidget from "@/components/dashboard/HouseViewWidget";
import { openExternal } from "@/lib/native/openExternal";

interface CalendarEvent {
  id: string;
  summary: string;
  start: {
    date?: string;
    dateTime?: string;
  };
  end: {
    date?: string;
    dateTime?: string;
  };
  description?: string;
  location?: string;
  htmlLink?: string;
}

interface BlogPost {
  id: string;
  title: string;
  excerpt: string | null;
  slug: string;
  published_at: string | null;
  category: string | null;
  featured_image_url: string | null;
  blog_types: string[];
}

const PartnerDashboard = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { user, staffMember, loading: authLoading } = useAuth();
  const [calendarEvents, setCalendarEvents] = useState<CalendarEvent[]>([]);
  const [calendarTimeRange, setCalendarTimeRange] = useState<string>('today');
  const [selectedTask, setSelectedTask] = useState<AsanaTask | null>(null);
  const [calendarFullscreen, setCalendarFullscreen] = useState(false);
  const [insightsPosts, setInsightsPosts] = useState<BlogPost[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([]);
  const [postsLoading, setPostsLoading] = useState(true);
  
  // Use the new Asana integration hook
  const { 
    tasks: asanaTasks, 
    loading: asanaLoading, 
    fetchTasks: fetchAsanaTasks, 
    updateTask: updateAsanaTask 
  } = useAsanaIntegration();

  const [googleCalendarToken, setGoogleCalendarToken] = useState<string | null>(null);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [expandedEventId, setExpandedEventId] = useState<string | null>(null);

  // Check if user is in Investment team
  const isInvestmentTeam = staffMember?.team === 'Investment';
  const canSeeHouseView = isInvestmentTeam || staffMember?.role_level === 'admin' || staffMember?.role_level === 'superadmin';

  // Live IG market data (Realtime subscription + edge-function poll fallback).
  // Hook always runs (rules of hooks); rendering stays gated on the Investment team.
  const { rows: marketData } = useMarketData();

  // --- Offline resilience for the IG market table (strictly additive) -------------------
  // Reactive reachability (web: navigator.onLine; native: @capacitor/network). Always online on the
  // public marketing site unless the browser reports otherwise — never hides live data.
  const isOnline = useIsOnline();

  // Stable cache key for last-known-good IG rows (namespaced under cbam.cache.* by persistentCache).
  const MARKET_CACHE_KEY = "partner.market.ig";

  // Seed a synchronous fallback from the persistent cache mirror at mount so a cold offline start can
  // paint saved prices instead of an empty "Loading…" strip. Read once (lazy initialiser).
  const [cachedMarket, setCachedMarket] = useState<{ rows: MarketRow[]; cachedAt: string | null }>(
    () => {
      const env = getCachedSync<MarketRow[]>(MARKET_CACHE_KEY);
      return env && Array.isArray(env.data)
        ? { rows: env.data, cachedAt: env.cachedAt }
        : { rows: [], cachedAt: null };
    },
  );

  // Write-through last-known-good whenever the live hook delivers a non-empty snapshot. This keeps the
  // persisted copy fresh for the next offline cold start. Fails soft (persistentCache never throws).
  useEffect(() => {
    if (marketData.length > 0) {
      void setCached(MARKET_CACHE_KEY, marketData);
      setCachedMarket({ rows: marketData, cachedAt: new Date().toISOString() });
    }
  }, [marketData]);

  // What the table renders: prefer the live snapshot; when it's empty (e.g. offline cold start or a
  // dropped Realtime channel), fall back to the last-known-good cached rows. When live data is
  // present the output is byte-identical to before — purely additive.
  const displayMarket = marketData.length > 0 ? marketData : cachedMarket.rows;

  // True only while we're showing cached rows because live data is unavailable. Drives a single
  // card-level "showing saved prices" affordance; per-row MarketStrip "stale" badges still apply.
  const showingCachedMarket = marketData.length === 0 && cachedMarket.rows.length > 0;
  const marketCachedLabel = useMemo(() => {
    if (!showingCachedMarket || !cachedMarket.cachedAt) return null;
    const d = new Date(cachedMarket.cachedAt);
    return Number.isNaN(d.getTime()) ? null : d.toLocaleTimeString();
  }, [showingCachedMarket, cachedMarket.cachedAt]);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/partner/login');
    }
  }, [authLoading, user, navigate]);

  useEffect(() => {
    if (user && staffMember) {
      console.log('User authenticated, fetching data...');
      
      // Only fetch Investment team specific data if user is on Investment team.
      // Market data now flows through the useMarketData hook (Realtime + fallback),
      // so no setInterval polling here anymore.
      if (staffMember.team === 'Investment') {
        fetchAsanaTasks();
        checkGoogleCalendarConnection();
      }

      // Fetch blog posts for all users
      fetchPosts();
    }
  }, [user, staffMember, fetchAsanaTasks]);

  const fetchPosts = async () => {
    setPostsLoading(true);
    try {
      // Fetch insights posts
      const { data: insights, error: insightsError } = await supabase
        .from('blog_posts')
        .select('id, title, excerpt, slug, published_at, category, featured_image_url, blog_types')
        .eq('status', 'published')
        .contains('blog_types', ['insights'])
        .order('published_at', { ascending: false })
        .limit(20);

      if (insightsError) {
        console.error('Error fetching insights:', insightsError);
      } else {
        console.log('Fetched insights posts:', insights?.length, insights?.map(p => p.title));
        setInsightsPosts(insights || []);
      }

      // Fetch public blog posts
      const { data: blogs, error: blogsError } = await supabase
        .from('blog_posts')
        .select('id, title, excerpt, slug, published_at, category, featured_image_url, blog_types')
        .eq('status', 'published')
        .contains('blog_types', ['public'])
        .order('published_at', { ascending: false })
        .limit(20);

      if (blogsError) {
        console.error('Error fetching blogs:', blogsError);
      } else {
        console.log('Fetched blog posts:', blogs?.length, blogs?.map(p => p.title));
        setBlogPosts(blogs || []);
      }
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setPostsLoading(false);
    }
  };

  const checkGoogleCalendarConnection = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session?.provider_token && session?.user?.app_metadata?.provider === 'google') {
        // Token is held in memory only; it does not persist across refreshes.
        // The user reconnects per session if the Supabase session lacks a provider_token.
        setGoogleCalendarToken(session.provider_token);
        setCalendarConnected(true);
        fetchCalendarEvents(session.provider_token, calendarTimeRange);
      }
    } catch (error) {
      console.error('Error checking Google Calendar connection:', error);
    }
  };


  const fetchCalendarEvents = async (token: string, timeRange: string = 'today') => {
    try {
      const { data, error } = await supabase.functions.invoke('google-calendar', {
        body: { action: 'get_events', token, timeRange }
      });

      if (error) throw error;

      if (data?.events) {
        setCalendarEvents(data.events);
      }
    } catch (error) {
      console.error('Error fetching calendar events:', error);
      toast({
        title: "Calendar Error",
        description: "Failed to fetch calendar events. Please try reconnecting.",
        variant: "destructive"
      });
    }
  };

  const handleCalendarConnected = (token: string) => {
    setGoogleCalendarToken(token);
    setCalendarConnected(true);
    fetchCalendarEvents(token, calendarTimeRange);
  };

  const handleTimeRangeChange = (timeRange: string) => {
    setCalendarTimeRange(timeRange);
    if (googleCalendarToken) {
      fetchCalendarEvents(googleCalendarToken, timeRange);
    }
  };

  const getTimeRangeLabel = (timeRange: string) => {
    switch (timeRange) {
      case 'today': return 'Today';
      case 'tomorrow': return 'Tomorrow';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'all': return 'All Upcoming';
      default: return 'Today';
    }
  };

  const handleCalendarDisconnect = () => {
    setGoogleCalendarToken(null);
    setCalendarConnected(false);
    setCalendarEvents([]);
  };

  const openTaskModal = (task: AsanaTask) => {
    setSelectedTask(task);
  };

  const handleTaskUpdate = async (completed: boolean) => {
    if (!selectedTask) return;
    
    const success = await updateAsanaTask(selectedTask.gid, { completed });
    if (success) {
      setSelectedTask(null);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No due date';
    return new Date(dateString).toLocaleDateString();
  };

  const formatCalendarDateTime = (event: CalendarEvent) => {
    const start = event.start.dateTime || event.start.date;
    if (!start) return '';
    
    const date = new Date(start);
    return date.toLocaleDateString() + (event.start.dateTime ? ' ' + date.toLocaleTimeString() : '');
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // Don't render dashboard if not authenticated
  if (!user || !staffMember) {
    return null;
  }

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 pb-8">

        <div className="container mx-auto px-4 py-8">
          {canSeeHouseView && (
            <div className="mb-8">
              <HouseViewWidget />
            </div>
          )}
          {/* Investment Team Only Content */}
          {isInvestmentTeam && (
            <>
            {/* Economic Calendar and Market Tracker Side by Side */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Economic Calendar - Left Half */}
            <Card className="shadow-lg border-0">
              <CardHeader style={{ backgroundColor: 'hsl(var(--widget-header-bg))', color: 'hsl(var(--widget-header-text))' }}>
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <CardTitle className="flex items-center gap-2 text-xl">
                      <Calendar className="h-6 w-6" />
                      Economic Calendar
                    </CardTitle>
                    <CardDescription style={{ color: 'hsl(var(--widget-header-text) / 0.8)' }}>
                      Important economic events and releases
                    </CardDescription>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setCalendarFullscreen(true)}
                    style={{ color: 'hsl(var(--widget-header-text))' }}
                    aria-label="Expand calendar to fullscreen"
                  >
                    <Maximize2 className="h-5 w-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-[600px] overflow-hidden rounded-b-lg">
                  <iframe
                    src="https://sslecal2.investing.com?columns=exc_flags,exc_currency,exc_importance,exc_actual,exc_forecast,exc_previous&features=datepicker,timezone,timeselector&countries=37,72,22,17,14,35,4,5&calType=day&timeZone=15&lang=51"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allowTransparency={true}
                    marginWidth={0}
                    marginHeight={0}
                    className="w-full h-full"
                  />
                </div>
              </CardContent>
            </Card>

            {calendarFullscreen && (
              <div className="fixed inset-0 z-[100] bg-background flex flex-col">
                <div
                  className="flex items-center justify-between px-4 py-3 border-b"
                  style={{ backgroundColor: 'hsl(var(--widget-header-bg))', color: 'hsl(var(--widget-header-text))' }}
                >
                  <div className="flex items-center gap-2 text-lg font-semibold">
                    <Calendar className="h-5 w-5" />
                    Economic Calendar
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setCalendarFullscreen(false)}
                    style={{ color: 'hsl(var(--widget-header-text))' }}
                  >
                    <Minimize2 className="h-4 w-4 mr-2" />
                    Exit fullscreen
                  </Button>
                </div>
                <div className="flex-1 overflow-hidden">
                  <iframe
                    src="https://sslecal2.investing.com?columns=exc_flags,exc_currency,exc_importance,exc_actual,exc_forecast,exc_previous&features=datepicker,timezone,timeselector&countries=37,72,22,17,14,35,4,5&calType=day&timeZone=15&lang=51"
                    width="100%"
                    height="100%"
                    frameBorder="0"
                    allowTransparency={true}
                    marginWidth={0}
                    marginHeight={0}
                    className="w-full h-full"
                  />
                </div>
              </div>
            )}

            {/* Market Tracker - Right Half */}
            <Card className="shadow-lg border-0">
              <CardHeader style={{ backgroundColor: 'hsl(var(--widget-header-bg))', color: 'hsl(var(--widget-header-text))' }}>
                <CardTitle className="flex items-center gap-2 text-xl">
                  <DollarSign className="h-6 w-6" />
                  Market Tracker
                </CardTitle>
                <CardDescription style={{ color: 'hsl(var(--widget-header-text) / 0.8)' }}>
                  Live market pricing data
                </CardDescription>
              </CardHeader>
              <CardContent>
                {showingCachedMarket && (
                  <div
                    role="status"
                    aria-live="polite"
                    className="mb-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-1.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-900/20 dark:text-amber-300"
                  >
                    {isOnline ? "Reconnecting" : "Offline"} — showing saved prices
                    {marketCachedLabel ? ` (as at ${marketCachedLabel})` : ""}
                  </div>
                )}
                <div className="space-y-2 max-h-[600px] overflow-y-auto">
                  {displayMarket.length > 0 ? (
                    displayMarket.map((market) => (
                      <MarketStrip key={market.ticker} row={market} />
                    ))
                  ) : (
                    <div className="text-center py-8 text-muted-foreground text-sm">
                      {isOnline ? "Loading market data..." : "Offline — no saved market data yet"}
                    </div>
                  )}
                </div>
                <div className="mt-4 text-xs text-muted-foreground">
                  * Live IG market data — updates in real time
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Calendar Events - Full Width */}
          <Card className="shadow-lg border-0 mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    Calendar Events - {getTimeRangeLabel(calendarTimeRange)}
                  </CardTitle>
                  <CardDescription>Your upcoming meetings and events</CardDescription>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={calendarTimeRange} onValueChange={handleTimeRangeChange}>
                    <SelectTrigger className="w-[140px] bg-background">
                      <SelectValue placeholder="Time Range" />
                    </SelectTrigger>
                    <SelectContent className="bg-popover border-border z-50">
                      <SelectItem value="today">Today</SelectItem>
                      <SelectItem value="tomorrow">Tomorrow</SelectItem>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="all">All Upcoming</SelectItem>
                    </SelectContent>
                  </Select>
                  <GoogleCalendarAuth 
                    onCalendarConnected={handleCalendarConnected}
                    isConnected={calendarConnected}
                    onDisconnect={handleCalendarDisconnect}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="max-h-96 overflow-y-auto">
                {calendarEvents.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Event</TableHead>
                        <TableHead>Date & Time</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {calendarEvents.map((event) => {
                        const isExpanded = expandedEventId === event.id;
                        const truncatedLocation = event.location && event.location.length > 50 
                          ? event.location.substring(0, 50) + '...' 
                          : event.location;
                        
                        return (
                          <>
                            <TableRow 
                              key={event.id} 
                              className="hover:bg-accent/10 cursor-pointer"
                              onClick={() => setExpandedEventId(isExpanded ? null : event.id)}
                            >
                              <TableCell>
                                <div className="font-medium">{event.summary}</div>
                                {isExpanded && event.description && (
                                  <div className="text-sm text-muted-foreground mt-2 p-2 bg-accent/20 rounded">
                                    {event.description}
                                  </div>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-sm">
                                  <Clock className="h-3 w-3" />
                                  {formatCalendarDateTime(event)}
                                </div>
                              </TableCell>
                              <TableCell>
                                {event.location ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const locationToUse = isExpanded ? event.location : truncatedLocation;
                                      const isUrl = event.location.startsWith('http://') || 
                                                   event.location.startsWith('https://') || 
                                                   event.location.includes('zoom.us') ||
                                                   event.location.includes('teams.microsoft.com') ||
                                                   event.location.includes('meet.google.com');
                                      
                                      if (isUrl) {
                                        void openExternal(event.location);
                                      } else {
                                        void openExternal(`https://maps.google.com/maps?q=${encodeURIComponent(event.location)}`);
                                      }
                                    }}
                                    className="text-sm text-primary hover:underline cursor-pointer flex items-center gap-1 text-left"
                                  >
                                    📍 {isExpanded ? event.location : truncatedLocation}
                                  </button>
                                ) : (
                                  <span className="text-sm text-muted-foreground">No location</span>
                                )}
                              </TableCell>
                              <TableCell className="text-right">
                                {event.htmlLink && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      void openExternal(event.htmlLink);
                                    }}
                                  >
                                    View in Calendar
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          </>
                        );
                      })}
                    </TableBody>
                  </Table>
                 ) : (
                   <div className="text-center py-8 text-muted-foreground">
                     {calendarConnected ? 
                       `No events found for ${getTimeRangeLabel(calendarTimeRange).toLowerCase()}` : 
                       'Connect your Google Calendar to see events'
                     }
                   </div>
                 )}
              </div>
            </CardContent>
          </Card>

          {/* Asana Tasks - Full Width */}
          <Card className="shadow-lg border-0">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Asana Tasks
                {asanaLoading && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>}
              </CardTitle>
              <CardDescription>Your assigned tasks from Asana</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {asanaTasks.length > 0 ? (
                  asanaTasks.map((task) => (
                    <div key={task.gid} className="p-3 border rounded-lg hover:bg-accent/10 transition-colors">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium">{task.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant={task.completed ? "default" : "secondary"} className={task.completed ? "bg-green-100 text-green-800 border-green-200" : ""}>
                              {task.completed ? "Completed" : "In Progress"}
                            </Badge>
                            <span className="text-sm text-muted-foreground">
                              Due: {formatDate(task.due_on)}
                            </span>
                          </div>
                          {task.projects && task.projects.length > 0 && (
                            <div className="mt-2">
                              {task.projects.map((project, index) => (
                                <Badge key={index} variant="outline" className="mr-1">
                                  {project.name}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <Dialog>
                            <DialogTrigger asChild>
                              <Button variant="ghost" size="sm" onClick={() => openTaskModal(task)}>
                                View
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="max-w-2xl">
                              <DialogHeader>
                                <DialogTitle>{selectedTask?.name}</DialogTitle>
                                <DialogDescription>
                                  Task details and actions
                                </DialogDescription>
                              </DialogHeader>
                              <div className="space-y-4">
                                <div>
                                  <h4 className="font-medium mb-2">Description</h4>
                                  <p className="text-sm text-muted-foreground border p-3 rounded bg-muted">
                                    {selectedTask?.notes || 'No description available'}
                                  </p>
                                </div>
                                
                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <h4 className="font-medium mb-2">Due Date</h4>
                                    <p className="text-sm">{formatDate(selectedTask?.due_on)}</p>
                                  </div>
                                  <div>
                                    <h4 className="font-medium mb-2">Status</h4>
                                    <Badge variant={selectedTask?.completed ? "default" : "secondary"} className={selectedTask?.completed ? "bg-green-100 text-green-800 border-green-200" : ""}>
                                      {selectedTask?.completed ? "Completed" : "In Progress"}
                                    </Badge>
                                  </div>
                                </div>

                                {selectedTask?.projects && selectedTask.projects.length > 0 && (
                                  <div>
                                    <h4 className="font-medium mb-2">Projects</h4>
                                    <div className="space-y-1">
                                      {selectedTask.projects.map((project, index) => (
                                        <Badge key={index} variant="outline">{project.name}</Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {selectedTask?.permalink_url && (
                                  <div className="flex justify-between items-center pt-4 border-t">
                                    <Button
                                      variant="outline"
                                      onClick={() => void openExternal(selectedTask.permalink_url)}
                                      className="flex items-center gap-2"
                                    >
                                      <ExternalLink className="h-4 w-4" />
                                      Open in Asana
                                    </Button>
                                    
                                    {!selectedTask.completed && (
                                      <Button
                                        onClick={() => handleTaskUpdate(true)}
                                        className="flex items-center gap-2"
                                      >
                                        <CheckCircle className="h-4 w-4" />
                                        Mark Complete
                                      </Button>
                                    )}
                                  </div>
                                )}
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    {asanaLoading ? 'Loading tasks...' : 'No tasks found. Configure your Asana integration in Profile Settings.'}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
          </>
          )}

          {/* Posts Scrollers - Visible to All Users */}
          <div className="space-y-8 mt-8">
            <MediaScroller />

            <PostsScroller
              title="Latest Insights"
              description="Recent insights and analysis for partners"
              posts={insightsPosts}
              type="insights"
              loading={postsLoading}
            />
            
            <PostsScroller
              title="Latest Blog Posts"
              description="Recent articles and updates"
              posts={blogPosts}
              type="blog"
              loading={postsLoading}
            />
          </div>
        </div>
      </div>
  );
};

export default PartnerDashboard;